import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { DefaultPalette } from '@fluentui/react';
import { Button, Divider, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';

import {
  cardContentWrapperStyles,
  cardTitleWrapperStyles,
  cardWrapperStyles,
  dividerStyles,
} from '../../../app/common/styles/CommonStyleObjects';

interface ICardProps {
  title: string;
  children: React.ReactNode;
  actionButton?: React.ReactNode;
  isLoading?: boolean;
  collapsable?: boolean;
  isCollapsed?: boolean;
  headingLevel?: number;
}

const Card: React.FunctionComponent<ICardProps> = ({
  title,
  children,
  actionButton,
  isLoading,
  headingLevel,
  collapsable,
  isCollapsed: collapseDefault,
}: ICardProps): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(collapseDefault);
  const isDataLoaded = isLoading === undefined ? true : !isLoading;
  const styles = dividerStyles();

  const onHeaderClick = () => {
    setIsCollapsed(!isCollapsed);
  };

  const cardActionButtonStyles = { padding: '0 21px' };

  const headerStyles = { display: 'flex', alignItems: 'center' };
  const buttonStyles = {
    marginRight: 10,
    color: DefaultPalette.blue,
  };

  const header = (): JSX.Element => {
    switch (headingLevel) {
      case 1:
        return (
          <div style={headerStyles}>
            {collapsable && (
              <Button
                appearance="subtle"
                style={buttonStyles}
                icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                title={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                onClick={(): void => {
                  onHeaderClick();
                }}
              />
            )}
            <h1>{title}</h1>
          </div>
        );
      case 2:
        return (
          <div style={headerStyles}>
            {collapsable && (
              <Button
                appearance="subtle"
                style={buttonStyles}
                icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                title={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                onClick={(): void => {
                  onHeaderClick();
                }}
              />
            )}
            <h2>{title}</h2>
          </div>
        );
      default:
        return (
          <div style={headerStyles}>
            {collapsable && (
              <Button
                appearance="subtle"
                style={buttonStyles}
                icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                title={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
                onClick={(): void => {
                  onHeaderClick();
                }}
              />
            )}
            <h3>{title}</h3>
          </div>
        );
    }
  };

  if (!isDataLoaded) {
    return (
      <Skeleton aria-label="Loading card content" className={`ms-depth-4 ${cardWrapperStyles}`}>
        <SkeletonItem size={128} />
      </Skeleton>
    );
  }

  return (
    <div className={`ms-depth-4 ${cardWrapperStyles}`}>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <div className={cardTitleWrapperStyles}>{header()}</div>
        {actionButton && <div style={cardActionButtonStyles}>{actionButton}</div>}
      </Stack>
      {(!collapsable || (collapsable && !isCollapsed)) && (
        <>
          <Divider className={styles.divider} />
          <div className={cardContentWrapperStyles}>{children}</div>
        </>
      )}
    </div>
  );
};

export default Card;
